import { useState, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { IconArrowLeft } from '@tabler/icons-react';
import { useAuth, isMfaChallenge, MfaSetup, User } from '../context/AuthContext';
import { FormGroup, Label, Input, ErrorAlert } from '../components/styled';

// ── Layout ─────────────────────────────────────────────────────────────────────

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing(6)};
  background: ${({ theme }) => theme.background.secondary};
`;

const Wrap = styled.div`
  width: 100%;
  max-width: 380px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(5)};
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  font-size: ${({ theme }) => theme.font.size.sm};
  font-weight: ${({ theme }) => theme.font.weight.medium};
  color: ${({ theme }) => theme.font.color.tertiary};
  text-decoration: none;
  transition: color 0.1s ease;

  &:hover {
    color: ${({ theme }) => theme.font.color.secondary};
  }
`;

const Card = styled.div`
  width: 100%;
  max-width: 380px;
  background: ${({ theme }) => theme.background.primary};
  border: 1px solid ${({ theme }) => theme.border.color.medium};
  border-radius: ${({ theme }) => theme.border.radius.lg};
  box-shadow: 0 10px 40px rgba(13, 37, 61, 0.08);
  padding: clamp(28px, 5vw, 40px);
`;

const Brand = styled.h1`
  font-size: 22px;
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  letter-spacing: -0.3px;
  color: ${({ theme }) => theme.font.color.primary};
  text-align: center;
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.font.color.tertiary};
  text-align: center;
  margin: ${({ theme }) => theme.spacing(1)} 0 ${({ theme }) => theme.spacing(8)} 0;
`;

const SubmitButton = styled.button`
  width: 100%;
  height: 44px;
  background: ${({ theme }) => theme.accent.primary};
  color: ${({ theme }) => theme.font.color.inverted};
  border: none;
  border-radius: ${({ theme }) => theme.border.radius.pill};
  font-size: ${({ theme }) => theme.font.size.md};
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  font-family: ${({ theme }) => theme.font.family};
  cursor: pointer;
  transition: background 0.1s ease, transform 0.15s ease;
  margin-top: ${({ theme }) => theme.spacing(4)};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.accent.accent10};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ForgotRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const ForgotLink = styled(Link)`
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.font.color.tertiary};
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.font.color.secondary};
  }
`;

const SignupRow = styled.p`
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.font.color.tertiary};
  text-align: center;
  margin: ${({ theme }) => theme.spacing(4)} 0 0;

  a {
    color: ${({ theme }) => theme.accent.primary};
    text-decoration: none;
    font-weight: ${({ theme }) => theme.font.weight.medium};

    &:hover {
      text-decoration: underline;
    }
  }
`;

const Notice = styled.p`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.font.color.secondary};
  line-height: 1.5;
  margin: 0 0 ${({ theme }) => theme.spacing(4)};
`;

const QrBox = styled.div`
  display: flex;
  justify-content: center;
  margin: 0 0 ${({ theme }) => theme.spacing(3)};

  img {
    width: 180px;
    height: 180px;
    border: 1px solid ${({ theme }) => theme.border.color.medium};
    border-radius: ${({ theme }) => theme.border.radius.md};
    background: #fff;
  }
`;

const SecretText = styled.code`
  display: block;
  text-align: center;
  font-size: ${({ theme }) => theme.font.size.xs};
  letter-spacing: 0.08em;
  word-break: break-all;
  color: ${({ theme }) => theme.font.color.secondary};
  background: ${({ theme }) => theme.background.secondary};
  border-radius: ${({ theme }) => theme.border.radius.md};
  padding: ${({ theme }) => theme.spacing(2)};
  margin: 0 0 ${({ theme }) => theme.spacing(4)};
`;

const CodeList = styled.ul`
  list-style: none;
  padding: ${({ theme }) => theme.spacing(3)};
  margin: 0 0 ${({ theme }) => theme.spacing(2)};
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing(1)} ${({ theme }) => theme.spacing(3)};
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.font.color.primary};
  background: ${({ theme }) => theme.background.secondary};
  border-radius: ${({ theme }) => theme.border.radius.md};
`;

const TextButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.accent.primary};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

// ── Component ─────────────────────────────────────────────────────────────────

// Only honor a return_to URL when it parses cleanly, stays on THIS origin, AND
// the path is /api/oauth/authorize (or /oauth/authorize). An absolute URL to
// another host used to slip past a path-only check, which is an open redirect.
function safeReturnTo(raw: string | null): string | null {
  if (!raw) return null;
  let parsed: URL;
  try {
    parsed = new URL(raw, window.location.origin);
  } catch {
    return null;
  }
  if (parsed.origin !== window.location.origin) return null;
  if (!/^\/(api\/)?oauth\/authorize$/.test(parsed.pathname)) return null;
  return parsed.pathname + parsed.search;
}

type Stage = 'credentials' | 'mfa' | 'setup' | 'recovery';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [unverified, setUnverified] = useState(false);
  const [resent, setResent] = useState(false);
  const [loading, setLoading] = useState(false);

  // Second factor
  const [stage, setStage] = useState<Stage>('credentials');
  const [mfaToken, setMfaToken] = useState('');
  const [code, setCode] = useState('');
  const [setup, setSetup] = useState<MfaSetup | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [pendingUser, setPendingUser] = useState<User | null>(null);
  const [copied, setCopied] = useState(false);

  const { login, completeMfa, startMfaSetup, finishMfaSetup } = useAuth();
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const justVerified = params.get('verified') === '1';

  function finishSignIn() {
    const returnTo = safeReturnTo(params.get('return_to'));
    if (returnTo) {
      // Full navigation (not React-router) — return_to points at the API's
      // /oauth/authorize, which is the OAuth re-entry path.
      window.location.href = returnTo;
      return;
    }
    navigate('/dashboard');
  }

  function backToCredentials(message: string) {
    setStage('credentials');
    setMfaToken('');
    setCode('');
    setSetup(null);
    setError(message);
  }

  async function handleResend() {
    try {
      await (await import('../services/api')).default.post('/auth/resend-verification', { email });
    } finally {
      setResent(true);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setUnverified(false);
    setResent(false);
    setLoading(true);

    try {
      const result = await login(email, password);
      if (isMfaChallenge(result)) {
        setMfaToken(result.mfaToken);
        setCode('');
        if (result.mfaSetupRequired) {
          setSetup(await startMfaSetup(result.mfaToken));
          setStage('setup');
        } else {
          setStage('mfa');
        }
        return;
      }
      finishSignIn();
    } catch (err) {
      if (err.response?.data?.unverified) {
        setUnverified(true);
        setError('Your email isn\'t verified yet — click the link we emailed you, or resend it below.');
      } else {
        setError(err.response?.data?.error || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleMfaSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await completeMfa(mfaToken, code);
      finishSignIn();
    } catch (err) {
      if (err.response?.data?.expired) {
        backToCredentials('That sign-in attempt timed out. Please sign in again.');
      } else {
        setError(err.response?.data?.error || 'That code didn\'t work — try again.');
        setCode('');
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleSetupSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await finishMfaSetup(mfaToken, code);
      setPendingUser(result.user);
      setRecoveryCodes(result.recoveryCodes);
      setStage('recovery');
    } catch (err) {
      if (err.response?.data?.expired) {
        backToCredentials('Setup timed out. Please sign in again to start over.');
      } else {
        setError(err.response?.data?.error || 'That code didn\'t work — check the app and try again.');
        setCode('');
      }
    } finally {
      setLoading(false);
    }
  }

  async function copyRecoveryCodes() {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join('\n'));
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  const codeField = (label: string) => (
    <FormGroup>
      <Label htmlFor="mfa-code">{label}</Label>
      <Input
        id="mfa-code"
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus
        value={code}
        onChange={(e) => setCode(e.target.value)}
        required
      />
    </FormGroup>
  );

  return (
    <Page>
      <Wrap>
        <Card>
          <Brand>Simkura</Brand>
          <Subtitle>
            {stage === 'credentials' && 'Fleet Management'}
            {stage === 'mfa' && 'Two-factor authentication'}
            {stage === 'setup' && 'Set up two-factor authentication'}
            {stage === 'recovery' && 'Save your recovery codes'}
          </Subtitle>

          {stage === 'credentials' && (
            <form onSubmit={handleSubmit}>
              {justVerified && !error && (
                <Notice>Email verified. Sign in to finish setting up two-factor authentication.</Notice>
              )}
              {error && <ErrorAlert>{error}</ErrorAlert>}
              {unverified && (
                <SignupRow style={{ marginTop: 0, marginBottom: 12 }}>
                  {resent
                    ? 'Verification email sent — check your inbox.'
                    : <a href="#resend" onClick={(e) => { e.preventDefault(); handleResend(); }}>Resend the verification email</a>}
                </SignupRow>
              )}
              <FormGroup>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </FormGroup>
              <ForgotRow>
                <ForgotLink to="/forgot-password">Forgot password?</ForgotLink>
              </ForgotRow>
              <SubmitButton type="submit" disabled={loading}>
                {loading ? 'Signing in...' : 'Sign in'}
              </SubmitButton>
              <SignupRow>
                New to Simkura? <Link to="/signup">Create an account</Link>
              </SignupRow>
            </form>
          )}

          {stage === 'mfa' && (
            <form onSubmit={handleMfaSubmit}>
              <Notice>Enter the 6-digit code from your authenticator app, or one of your recovery codes.</Notice>
              {error && <ErrorAlert>{error}</ErrorAlert>}
              {codeField('Code')}
              <SubmitButton type="submit" disabled={loading || code.trim().length < 6}>
                {loading ? 'Checking...' : 'Continue'}
              </SubmitButton>
              <SignupRow>
                <a href="#back" onClick={(e) => { e.preventDefault(); backToCredentials(''); }}>Use a different account</a>
              </SignupRow>
            </form>
          )}

          {stage === 'setup' && setup && (
            <form onSubmit={handleSetupSubmit}>
              <Notice>
                Two-factor authentication is required. Scan this QR code with an authenticator app
                (Google Authenticator, Microsoft Authenticator, 1Password…), then enter the code it shows.
              </Notice>
              {error && <ErrorAlert>{error}</ErrorAlert>}
              <QrBox>
                <img src={setup.qrDataUrl} alt="QR code for your authenticator app" />
              </QrBox>
              <Notice style={{ marginBottom: 4 }}>Can't scan? Enter this key manually:</Notice>
              <SecretText>{setup.secret.replace(/(.{4})/g, '$1 ').trim()}</SecretText>
              {codeField('Code from the app')}
              <SubmitButton type="submit" disabled={loading || code.trim().length < 6}>
                {loading ? 'Verifying...' : 'Turn on two-factor authentication'}
              </SubmitButton>
              <SignupRow>
                <a href="#back" onClick={(e) => { e.preventDefault(); backToCredentials(''); }}>Cancel</a>
              </SignupRow>
            </form>
          )}

          {stage === 'recovery' && (
            <div>
              <Notice>
                Two-factor authentication is on. Keep these one-time recovery codes somewhere safe —
                each one signs you in once if you lose access to your authenticator app. They won't be shown again.
              </Notice>
              <CodeList>
                {recoveryCodes.map((c) => <li key={c}>{c}</li>)}
              </CodeList>
              <SignupRow style={{ marginTop: 0 }}>
                <TextButton type="button" onClick={copyRecoveryCodes}>
                  {copied ? 'Copied to clipboard' : 'Copy all codes'}
                </TextButton>
              </SignupRow>
              <SubmitButton type="button" onClick={finishSignIn} disabled={!pendingUser}>
                I've saved my codes — continue
              </SubmitButton>
            </div>
          )}
        </Card>
        <BackLink to="/">
          <IconArrowLeft size={15} /> Back to home
        </BackLink>
      </Wrap>
    </Page>
  );
}

export default Login;
